/**
 * Product FAQ Integration Manager
 * Dynamically injects product-specific FAQs into product tabs
 * Handles product transitions and prevents duplicate FAQ rendering
 */
if (!window.Eurus.loadedScript.includes("product-faq-integration.js")) {
  window.Eurus.loadedScript.push("product-faq-integration.js");

  class ProductFAQManager {
    constructor() {
      this.processedContainers = new Set();
      this.isProcessing = false;
      this.isCompleted = false;
      this.faqData = null;
      this.currentProductHandle = null;
      this.init();
    }

    /**
     * Initialize the FAQ manager
     * Sets up event listeners and executes initial integration
     */
    init() {
      this.bindEvents();
      this.executeIntegration();
    }

    /**
     * Bind event listeners for dynamic content loading
     * Handles Alpine.js initialization and page navigation
     */
    bindEvents() {
      document.addEventListener("alpine:init", () => {
        setTimeout(() => this.safeExecute(), 500);
      });

      setTimeout(() => this.safeExecute(), 1000);
      window.addEventListener("load", () => {
        setTimeout(() => this.safeExecute(), 800);
      });

      // Listen for browser back/forward navigation
      window.addEventListener("popstate", () => {
        this.handleProductChange();
      });

      // Intercept programmatic navigation calls
      this.observeUrlChanges();
    }

    /**
     * Override history API methods to detect product changes
     * Ensures FAQ updates on AJAX navigation and URL changes
     */
    observeUrlChanges() {
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      const self = this;

      history.pushState = function (...args) {
        originalPushState.apply(this, args);
        self.handleProductChange();
      };

      history.replaceState = function (...args) {
        originalReplaceState.apply(this, args);
        self.handleProductChange();
      };
    }

    /**
     * Handle product changes during navigation
     * Clears existing FAQs and reinitializes for new product
     */
    handleProductChange() {
      const newHandle = this.getProductHandle();

      // Detect product change and reset state
      if (
        newHandle &&
        this.currentProductHandle &&
        this.currentProductHandle !== newHandle
      ) {
        // Remove old FAQ content to prevent duplicates
        this.removeExistingFaqs();
        this.processedContainers.clear();
        this.isCompleted = false;
        this.faqData = null;
        this.currentProductHandle = newHandle;

        // Re-initialize FAQ integration with delay
        setTimeout(() => this.safeExecute(), 300);
      } else if (!this.currentProductHandle && newHandle) {
        this.currentProductHandle = newHandle;
      }
    }

    /**
     * Clean up existing FAQ DOM elements
     * Removes tabs, content, and structured data
     */
    removeExistingFaqs() {
      const existingFaqTabs = document.querySelectorAll(".faq-tab-added");
      existingFaqTabs.forEach((tab) => tab.remove());

      const existingFaqContent =
        document.querySelectorAll(".faq-content-added");
      existingFaqContent.forEach((content) => content.remove());

      const existingFaqScript = document.querySelector(
        'script[type="application/ld+json"][data-faq-structured]'
      );
      if (existingFaqScript) {
        existingFaqScript.remove();
      }
    }

    /**
     * Safe execution wrapper
     * Prevents concurrent processing and duplicate executions
     */
    safeExecute() {
      if (this.isProcessing || this.isCompleted) {
        return;
      }
      this.executeIntegration();
    }

    /**
     * Main integration execution
     * Processes all product tab containers on the page
     */
    async executeIntegration() {
      if (this.isProcessing) return;

      this.isProcessing = true;

      const containers = document.querySelectorAll('[x-data="xProductTabs()"]');

      for (const container of containers) {
        await this.processContainer(container);
      }

      this.isProcessing = false;
      this.isCompleted = true;
    }

    /**
     * Process individual tab container
     * Checks for existing FAQs and creates new ones if needed
     */
    async processContainer(container) {
      const sectionElement = container.closest('[class*="product-tab"]');
      if (!sectionElement) return;

      const sectionId = this.extractSectionId(sectionElement);
      const tabsContainer = container.querySelector(
        `[class*="tabs-${sectionId}"]`
      );
      const contentContainer = container.querySelector(
        `[class*="content-tabs-${sectionId}"]`
      );

      if (!tabsContainer || !contentContainer) return;

      const productHandle = this.getProductHandle();
      if (!productHandle) return;

      // Skip if FAQ tab already exists
      if (this.hasExistingFaqTab(tabsContainer, container)) {
        return;
      }

      // Create unique ID combining container, product, and section
      const containerId = this.extractContainerId(sectionElement);
      const uniqueId = `${containerId}-${productHandle}-${sectionId}`;

      // Skip if already processed
      if (this.processedContainers.has(uniqueId)) {
        return;
      }

      try {
        const faqs = await this.loadFaqData(productHandle);
        if (faqs.length > 0) {
          const tabIndex = tabsContainer.querySelectorAll(".tab-item").length;
          this.createFaqTab(sectionId, tabIndex, faqs, container);
          this.processedContainers.add(uniqueId);
        }
      } catch (error) {
        console.error("FAQ loading error:", error);
      }
    }

    /**
     * Extract container ID from element classes
     * @param {HTMLElement} element
     * @returns {string}
     */
    extractContainerId(element) {
      const match = element.className.match(/bg-([^\s]+)/);
      return match ? match[1] : "default";
    }

    /**
     * Extract section ID from template classes
     * @param {HTMLElement} element
     * @returns {string}
     */
    extractSectionId(element) {
      const classes = Array.from(element.classList);
      const bgClass = classes.find((cls) => cls.includes("bg-template--"));
      return bgClass ? bgClass.replace("bg-", "") : "default";
    }

    /**
     * Check if FAQ tab already exists in container
     * @param {HTMLElement} tabsContainer
     * @param {HTMLElement} container
     * @returns {boolean}
     */
    hasExistingFaqTab(tabsContainer, container) {
      const existingFaqTabs = tabsContainer.querySelectorAll(
        '[data-tabtitle="FAQ"]'
      );
      const existingFaqTabsAdded =
        tabsContainer.querySelectorAll(".faq-tab-added");
      const existingFaqContent =
        container.querySelectorAll(".faq-content-added");

      return (
        existingFaqTabs.length > 0 ||
        existingFaqTabsAdded.length > 0 ||
        existingFaqContent.length > 0
      );
    }

    /**
     * Get product handle from multiple sources
     * Tries URL, inline JSON, and meta tags in order
     * @returns {string|null}
     */
    getProductHandle() {
      const urlPath = window.location.pathname;
      const productUrlMatch = urlPath.match(/\/products\/([^\/\?]+)/);
      if (productUrlMatch) return productUrlMatch[1];

      const productDataScript = document.querySelector(
        'script[type="application/json"][data-product-json]'
      );
      if (productDataScript) {
        try {
          const productData = JSON.parse(productDataScript.textContent);
          return productData.handle;
        } catch (e) {
          return null;
        }
      }

      const productMeta = document.querySelector('meta[property="og:url"]');
      if (productMeta) {
        const metaUrl = productMeta.getAttribute("content");
        const metaMatch = metaUrl.match(/\/products\/([^\/\?]+)/);
        if (metaMatch) return metaMatch[1];
      }

      return null;
    }

    /**
     * Load FAQ data from JSON file
     * Caches data for subsequent requests
     * @param {string} productHandle
     * @returns {Promise<Array>}
     */
    async loadFaqData(productHandle) {
      if (this.faqData) {
        return this.findProductFaq(productHandle);
      }

      try {
        const response = await fetch(window.Eurus.productFaqUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        this.faqData = await response.json();
        return this.findProductFaq(productHandle);
      } catch (error) {
        console.error("Failed to load FAQ data:", error);
        return [];
      }
    }

    /**
     * Find FAQ data for specific product handle
     * @param {string} productHandle
     * @returns {Array}
     */
    findProductFaq(productHandle) {
      if (!this.faqData || !Array.isArray(this.faqData)) {
        return [];
      }
      const productFAQ = this.faqData.find(
        (item) => item.handle === productHandle
      );
      if (productFAQ && productFAQ.faq) {
        console.log(
          `FAQ trouvées pour ${productHandle}:`,
          productFAQ.faq.length,
          "questions"
        );
        return productFAQ.faq;
      } else {
        console.log(`Aucune FAQ trouvée pour ${productHandle}`);
        return [];
      }
    }

    /**
     * Create and inject FAQ tab and content
     * @param {string} sectionId
     * @param {number} tabIndex
     * @param {Array} faqs
     * @param {HTMLElement} container
     */
    createFaqTab(sectionId, tabIndex, faqs, container) {
      const tabTemplate = this.createTabTemplate(sectionId, tabIndex);
      const contentTemplate = this.createContentTemplate(tabIndex, faqs);

      const tabsTarget = container.querySelector(`.tabs-${sectionId}`);
      const contentTarget = container.querySelector(
        `.content-tabs-${sectionId}`
      );

      if (tabsTarget && contentTarget) {
        tabsTarget.appendChild(tabTemplate);
        contentTarget.appendChild(contentTemplate);
        this.refreshSplide(container);

        // Generate structured data for SEO
        this.generateStructuredData(faqs);
      }
    }

    /**
     * Create FAQ tab button template
     * @param {string} sectionId
     * @param {number} tabIndex
     * @returns {DocumentFragment}
     */
    createTabTemplate(sectionId, tabIndex) {
      const template = document.createElement("template");
      template.innerHTML = `
        <div class="tab-item tab-${tabIndex} border-tab-${sectionId} md:w-auto pt-1.5 pb-1.5 pl-4 md:pl-4 md:inline-block md:mr-1 mr-1 pr-4 md:pr-4 splide__slide x-splide-slide inline-block faq-tab-added"
             @click="open = ${tabIndex}, setTabActive(), openMobile = false" 
             :class="open == ${tabIndex} ? 'tab-enabled' : 'tab-disable cursor-pointer opacity-60'"
             x-init="open == ${tabIndex} && setTabActive()"
             data-tabtitle="FAQ">
          <span :class="openMobile ? '' : 'line-clamp-1'">FAQ</span>
        </div>
      `;
      return template.content.cloneNode(true);
    }

    /**
     * Create FAQ content container template
     * @param {number} tabIndex
     * @param {Array} faqs
     * @returns {DocumentFragment}
     */
    createContentTemplate(tabIndex, faqs) {
      const template = document.createElement("template");
      const faqHTML = faqs
        .map((faq, index) => this.createFaqAccordion(faq, index))
        .join("");

      template.innerHTML = `
        <div class="content-${tabIndex} rte text-left faq-content-added" x-cloak x-show="open == ${tabIndex}" style="text-align: left !important;">
          <div class="faq-accordions space-y-4" style="text-align: left !important;">
            ${faqHTML}
          </div>
        </div>
      `;
      return template.content.cloneNode(true);
    }

    /**
     * Create individual FAQ accordion item
     * @param {Object} faq
     * @param {number} index
     * @returns {string}
     */
    createFaqAccordion(faq, index) {
      const isFirstOpen = index === 0;
      const formattedAnswer = faq.reponse.replace(
        /\*\*(.*?)\*\*/g,
        "<strong>$1</strong>"
      );

      return `
        <div class="faq-accordion border border-solid border-[rgb(var(--colors-text),0.2)] rounded-lg" x-data="{open: ${isFirstOpen}}" style="text-align: left !important;">
          <button class="w-full flex justify-between items-center p-4 text-left hover:bg-[rgb(var(--colors-background),0.5)] transition-colors duration-200"
                  @click="open = !open" style="text-align: left !important;">
            <span class="font-medium text-[rgb(var(--colors-text))] pr-4 text-left" style="text-align: left !important;">${faq.question}</span>
            <span class="w-5 h-5 flex-shrink-0 transition-transform duration-300" :class="open ? 'rotate-180' : ''">
              <svg class="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </span>
          </button>
          <div class="border-t border-solid border-[rgb(var(--colors-text),0.1)]" x-show="open" x-collapse x-cloak style="text-align: left !important;">
            <div class="p-4 text-[rgb(var(--colors-text))] leading-relaxed text-left" style="text-align: left !important;">
              ${formattedAnswer}
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Refresh Splide carousel instance
     * @param {HTMLElement} container
     */
    refreshSplide(container) {
      const splideContainer = container.querySelector(".splide");
      if (splideContainer && window.Splide) {
        const splideInstance = splideContainer.splide;
        if (splideInstance) {
          splideInstance.refresh();
        }
      }
    }

    /**
     * Generate schema.org FAQPage structured data
     * @param {Array} faqs
     */
    generateStructuredData(faqs) {
      if (!faqs || faqs.length === 0) return;

      // Check if structured data already exists
      const existingFaqScript = document.querySelector(
        'script[type="application/ld+json"][data-faq-structured]'
      );
      if (existingFaqScript) {
        console.log("Données structurées FAQ déjà présentes");
        return;
      }

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.reponse.replace(/\*\*(.*?)\*\*/g, "$1"),
          },
        })),
      };

      // Create and inject JSON-LD script
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-faq-structured", "true");
      script.textContent = JSON.stringify(structuredData, null, 2);

      // Insert into head or body
      const targetElement = document.head || document.body;
      if (targetElement) {
        targetElement.appendChild(script);
        console.log(
          "Données structurées FAQPage générées:",
          faqs.length,
          "questions"
        );
      }
    }
  }

  // Global state management
  window.faqIntegrationProcessed = window.faqIntegrationProcessed || new Set();
  window.faqIntegrationRunning = window.faqIntegrationRunning || false;
  window.faqIntegrationCompleted = window.faqIntegrationCompleted || false;

  // Initialize manager (defer ensures DOM is ready)
  new ProductFAQManager();
}
